import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
  useMemo,
  useLayoutEffect,
  forwardRef,
} from "react";
import { VariableSizeList as List, ListChildComponentProps } from "react-window";
import { v4 as uuidv4 } from "uuid";
import useUrlParams from "../../../../hooks/useUrlParams";
import "./Console.scss";
import { getEnvVar } from "../../../../../../envUtils";
import { ArrowUp } from "./ConsoleSvg";
import useTrueTwin from "../../../../hooks/useTrueTwin";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import { fetchEntries } from "./Utils";
import ShimmerMenuList from "../../../../../CommonComponents/ShimmerEffectDesign/ShimmerMenu";
import ShimmerCell from "../../../../../CommonComponents/ShimmerEffectDesign/ShimmerCell";
import useConsole from "../../../../hooks/useConsole";
import { smoothScroll } from "./helpers/scrollHelpers";
import { handleUnreadBadgeClick } from "./helpers/consoleUnreadHelpers";
import TestPlanHeader from "./components/TestPlanHeader";
import { showToastMessage } from "../../../../../../utils/commonFunctions";
import { setTelecommandResult } from "../../../../../validate/api/validate.api";
import { CriticalIcon } from "../../../../../Missions/Satellites/TestCase/svgs/TestCaseSvgs";
import { deriveIsCritical } from "../../../../../Missions/Satellites/TestCase/TestCaseHelpers";
import { isValidateReloadEvent } from "../../../../../validate/helpers/validateWsHelpers";
import type {
  ConsoleTableColumn,
  ConsoleEntry,
  SelectedRowState,
} from "./types/consoleTypes";
import type { AllFilterType } from "../../../../../CommonComponents/custom_filter/types";
import useScenario from "../../../../hooks/useScenario";
import { SCENARIO_DEPLOYMENT_TYPES } from "../../../../../simulate/constants";

// ─── Row height constants ─────────────────────────────────────────────────────

const BASE_ROW_HEIGHT = 33;
const GROUP_HEADER_HEIGHT = 32;
const GROUP_FOOTER_HEIGHT = 2;

function isCommandIdentifier(val: unknown, entry?: ConsoleEntry): boolean {
  if (!val) return false;
  const s = String(val).trim();
  if (!s) return false;
  if (entry?.CommandUid && s === String(entry.CommandUid).trim()) return true;
  if (entry?.CommandDetails?.tctmId && s === String(entry.CommandDetails.tctmId).trim()) return true;
  if (/^\d+$/.test(s)) return true;
  return false;
}

function hasValidateGroupMetadata(entry: ConsoleEntry | undefined): boolean {
  if (!entry) return false;
  const testPlanResultId = String(entry.testPlanResultId ?? entry.testPlan ?? "").trim();
  const testPlanName = String(entry.testPlanName ?? "").trim();
  const testPlanId = String((entry as any)?.testPlanId ?? "").trim();
  const testCaseResultId = String(entry.testCaseResultId ?? "").trim();
  const testCaseName = String(entry.testCaseName ?? "").trim();
  const testCaseId = String(entry.testCaseId ?? "").trim();

  const hasPlan = Boolean(
    (testPlanResultId && !isCommandIdentifier(testPlanResultId, entry)) ||
    (testPlanName && !isCommandIdentifier(testPlanName, entry)) ||
    (testPlanId && !isCommandIdentifier(testPlanId, entry))
  );

  const hasCase = Boolean(
    (testCaseResultId && !isCommandIdentifier(testCaseResultId, entry)) ||
    (testCaseName && !isCommandIdentifier(testCaseName, entry)) ||
    (testCaseId && !isCommandIdentifier(testCaseId, entry))
  );

  return hasPlan || hasCase;
}

function getValidateGroupKey(entry: ConsoleEntry | undefined): string {
  if (!hasValidateGroupMetadata(entry)) return "";

  // 1. Resolve Plan Key: prioritize testPlanResultId (common across the test plan run)
  const rawPlanResultId = String(entry?.testPlanResultId ?? "").trim();
  const rawLegacyPlan = String(entry?.testPlan ?? "").trim();
  const rawPlanId = String((entry as any)?.testPlanId ?? "").trim();
  const rawPlanName = String(entry?.testPlanName ?? "").trim();

  let planKey = "";
  if (rawPlanResultId && !isCommandIdentifier(rawPlanResultId, entry)) {
    planKey = rawPlanResultId;
  } else if (rawLegacyPlan && !isCommandIdentifier(rawLegacyPlan, entry)) {
    planKey = rawLegacyPlan;
  } else if (rawPlanId && !isCommandIdentifier(rawPlanId, entry)) {
    planKey = rawPlanId;
  } else if (rawPlanName && !isCommandIdentifier(rawPlanName, entry)) {
    planKey = rawPlanName;
  }

  // 2. Resolve Case Key: prioritize testCaseResultId (unique for each test case run)
  const rawCaseResultId = String(entry?.testCaseResultId ?? "").trim();
  const rawCaseId = String(entry?.testCaseId ?? "").trim();
  const rawCaseName = String(entry?.testCaseName ?? "").trim();

  let caseKey = "";
  if (rawCaseResultId && !isCommandIdentifier(rawCaseResultId, entry)) {
    caseKey = rawCaseResultId;
  } else if (rawCaseId && !isCommandIdentifier(rawCaseId, entry)) {
    caseKey = rawCaseId;
  } else if (rawCaseName && !isCommandIdentifier(rawCaseName, entry)) {
    caseKey = rawCaseName;
  }

  // 3. Composite grouping when both exist, otherwise fallback to whichever is present
  if (planKey && caseKey) return `${planKey}::${caseKey}`;
  if (caseKey) return caseKey;
  if (planKey) return planKey;

  return "";
}

function getValidateGroupResult(
  entry: ConsoleEntry | undefined,
  testPlanResults?: Record<string, boolean | string>
): boolean | string | undefined {
  if (!entry || !testPlanResults) return undefined;
  const groupKey = getValidateGroupKey(entry);
  if (groupKey && testPlanResults[groupKey] !== undefined) return testPlanResults[groupKey];

  const caseResultId = String(entry.testCaseResultId ?? "").trim();
  if (caseResultId && testPlanResults[caseResultId] !== undefined) return testPlanResults[caseResultId];

  const planResultId = String(entry.testPlanResultId ?? entry.testPlan ?? "").trim();
  if (planResultId && testPlanResults[planResultId] !== undefined) return testPlanResults[planResultId];

  const caseId = String(entry.testCaseId ?? "").trim();
  if (caseId && testPlanResults[caseId] !== undefined) return testPlanResults[caseId];

  const planId = String((entry as any)?.testPlanId ?? "").trim();
  if (planId && testPlanResults[planId] !== undefined) return testPlanResults[planId];

  return undefined;
}

function getValidateGroupResultKey(entry: ConsoleEntry | undefined): string {
  return getValidateGroupKey(entry);
}

function checkGroupHasResults(index: number, entries: ConsoleEntry[], currentGroupKey: string) {
  let hasPass = false;
  let hasFail = false;
  let hasTimeout = false;

  const check = (e: ConsoleEntry) => {
    const res = e.result ?? e.CommandDetails?.result ?? (e as any).Result ?? (e.CommandDetails as any)?.Result;
    if (res === true || String(res).toLowerCase() === 'pass' || String(res).toLowerCase() === 'passed') hasPass = true;
    if (res === false || String(res).toLowerCase() === 'fail' || String(res).toLowerCase() === 'failed') hasFail = true;
    if (String(res).toLowerCase() === 'timeout' || String(res).toLowerCase() === 'timed_out' || String(res).toLowerCase() === 'timedout') hasTimeout = true;
  };

  for (let i = index; i >= 0; i--) {
    const e = entries[i];
    if (getValidateGroupKey(e) !== currentGroupKey) break;
    check(e);
  }

  for (let i = index + 1; i < entries.length; i++) {
    const e = entries[i];
    if (getValidateGroupKey(e) !== currentGroupKey) break;
    check(e);
  }

  return { hasPass, hasFail, hasTimeout };
}

function checkGroupIsOnboard(index: number, entries: ConsoleEntry[], currentGroupKey: string): boolean {
  const check = (e: ConsoleEntry) => {
    const loc = e.executionLocation ?? (e.CommandDetails as any)?.executionLocation;
    if (typeof loc === 'string' && loc.trim().toUpperCase() === 'ONBOARD') return true;
    if (Boolean(e.executeOnboard) || Boolean((e.CommandDetails as any)?.executeOnboard)) return true;
    return false;
  };

  for (let i = index; i >= 0; i--) {
    const e = entries[i];
    if (getValidateGroupKey(e) !== currentGroupKey) break;
    if (check(e)) return true;
  }

  for (let i = index + 1; i < entries.length; i++) {
    const e = entries[i];
    if (getValidateGroupKey(e) !== currentGroupKey) break;
    if (check(e)) return true;
  }

  return false;
}

function getEntryResultStateKey(entry: ConsoleEntry | undefined): string {
  if (!entry) return "";
  const cursorId = String(entry["cursorId"] ?? "").trim();
  if (cursorId) return cursorId;

  // Fallback to the frontend-assigned row id so optimistic Pass/Fail updates
  // stay scoped to the exact rendered row even when multiple rows share the
  // same CommandUid and metadata.
  const uniqueId = String(entry.uniqueId ?? "").trim();
  if (uniqueId) return uniqueId;

  return [
    String(entry.CommandUid ?? "").trim(),
    String(entry.type ?? "").trim(),
    String(entry.CommandDetails?.timestamp ?? "").trim(),
    String(entry.CommandDetails?.tctmId ?? "").trim(),
    String(entry.TaskName ?? "").trim(),
    String(entry.Info ?? "").trim(),
  ].join("::");
}

/** Computes dynamic height for each row depending on its test-plan group position. */
function getItemSize(index: number, entries: ConsoleEntry[], isGroupingEnabled: boolean = false): number {
  const entry = entries[index];
  const prev = entries[index - 1];
  const next = entries[index + 1];
  if (!entry || !isGroupingEnabled) return BASE_ROW_HEIGHT;

  const groupKey = getValidateGroupKey(entry);
  const prevGroupKey = getValidateGroupKey(prev);
  const nextGroupKey = getValidateGroupKey(next);

  const hasTestPlan = groupKey !== "";
  const isStartOfGroup = hasTestPlan && (!prev || groupKey !== prevGroupKey);
  const isEndOfGroup = hasTestPlan && (!next || groupKey !== nextGroupKey);

  let size = BASE_ROW_HEIGHT;
  if (isStartOfGroup) size += GROUP_HEADER_HEIGHT;
  if (isEndOfGroup) size += GROUP_FOOTER_HEIGHT;
  return size;
}

// ─── TCTM type → CSS class ────────────────────────────────────────────────────

const TCTM_TYPE_CLASSES: Record<string, string> = {
  Beacon: "bg-typeBeacon",
  "Health Metric": "bg-typeHealthMetric",
  TCTM: "bg-typeTCTM",
  TC: "bg-typeTC",
  TM: "bg-typeTM",
  "Invalid TM": "bg-invalidTM",
};

// ─── TableCell ────────────────────────────────────────────────────────────────

interface TableCellProps {
  width: string;
  content: string | number | undefined;
  children?: React.ReactNode;
  className?: string;
  align?: "start" | "center" | "end";
}

const TableCell: React.NamedExoticComponent<TableCellProps> = memo<TableCellProps>(
  ({ width, content, children, className = "", align = "start" }) => {
    const isLoading = children === undefined && (content === undefined || content === null);
    const widthStr = String(width ?? "");
    const isIconCol = align === "center";
    const parsedWidth = widthStr.includes("px") || widthStr === "flex" || widthStr === "100%"
      ? (isIconCol ? "50px" : widthStr)
      : `${widthStr}px`;

    const minW = isIconCol
      ? "50px"
      : (parsedWidth !== "flex" && parsedWidth !== "100%" && parsedWidth !== "flexpx" && parsedWidth !== "100%px"
          ? parsedWidth
          : "100px");

    const cellStyle: React.CSSProperties = isIconCol
      ? { width: "50px", minWidth: "50px", flexShrink: 0, padding: "2px 4px" }
      : {
          flex: "1 1 0px",
          minWidth: minW,
          width: minW,
          overflow: "hidden",
          padding: "2px 8px",
          boxSizing: "border-box",
        };

    return (
      <div
        className={`d-flex align-items-center justify-content-${align} ${className}`}
        style={cellStyle}
      >
        {isLoading ? (
          <ShimmerCell width="80%" height="16px" />
        ) : children !== undefined ? (
          children
        ) : (
          <CustomToolTip title={String(content ?? "")} placement="top" className="console-table-cell__tooltip">
            <span className="truncate__text" style={{ minWidth: 0 }}>{content}</span>
          </CustomToolTip>
        )}
      </div>
    );
  }
);
TableCell.displayName = "ConsoleTableCell";

// ─── RowItemData ──────────────────────────────────────────────────────────────

interface RowItemData {
  entries: ConsoleEntry[];
  selectedRow: SelectedRowState;
  getCommandDetails: (entry: ConsoleEntry) => void;
  show_selected_output_details: (entry: ConsoleEntry | null, entries: ConsoleEntry[]) => void;
  consoleTableColumns: ConsoleTableColumn[];
  setIsOutputModalVisible: (v: boolean) => void;
  isOutputModalVisible: boolean;
  commandDetails?: { data?: ConsoleEntry[] } | null;
  showCorrespondingTask?: boolean;
  setIsFrozen: (v: boolean) => void;
  setDataCount: React.Dispatch<React.SetStateAction<number>>;
  listRef: React.RefObject<unknown>;
  height: number;
  isFrozen: boolean;
  frozenData: ConsoleEntry[] | null;
  removeCommandUidForHighlight: (id: string | undefined) => void;
  highlightedCommands: Set<string>;
  dashboard: string | undefined;
  filteredColumns: ConsoleTableColumn[];
  /** Pass/Fail/Timeout state map: testPlanId → result (validate mode only) */
  testPlanResults: Record<string, boolean | string>;
  setTestPlanResults: React.Dispatch<React.SetStateAction<Record<string, boolean | string>>>;
  setEntries: React.Dispatch<React.SetStateAction<ConsoleEntry[]>>;
  /** True when scenario deploymentType is validate */
  isValidateMode: boolean;
  isGroupingEnabled?: boolean;
  /** Pending per-entry results — survives API refresh until the API call resolves */
  pendingEntryResultsRef: React.MutableRefObject<Map<string, boolean | string>>;
  /** Pending group-level results — survives API refresh until the API call resolves */
  pendingGroupResultsRef: React.MutableRefObject<Map<string, boolean | string>>;
}

// ─── Row ──────────────────────────────────────────────────────────────────────

/** Single (possibly grouped) row in the virtualized console messages list */
const Row = memo<ListChildComponentProps<RowItemData>>(
  ({ index, style, data }) => {
    const {
      entries,
      selectedRow,
      getCommandDetails,
      show_selected_output_details,
      consoleTableColumns,
      setIsOutputModalVisible,
      isOutputModalVisible,
      commandDetails,
      showCorrespondingTask = false,
      setIsFrozen,
      setDataCount,
      listRef,
      height,
      isFrozen,
      frozenData,
      removeCommandUidForHighlight,
      highlightedCommands,
      dashboard,
      filteredColumns,
      testPlanResults,
      setEntries,
      isValidateMode,
      isGroupingEnabled: propIsGroupingEnabled,
      pendingEntryResultsRef,
    } = data;

    const isOperateDashboard =
      (typeof window !== "undefined" &&
        window.location.pathname.toLowerCase().includes("operate-dashboard"));
    const isGroupingEnabled =
      propIsGroupingEnabled ?? (isValidateMode && !isOperateDashboard);

    let entry: ConsoleEntry | undefined;
    if (showCorrespondingTask && commandDetails?.data) {
      entry = commandDetails.data.find((e) => e?.type !== selectedRow?.type) as ConsoleEntry | undefined;
    } else {
      entry = entries[index];
    }

    const current = entries[index];
    const prev = entries[index - 1];
    const next = entries[index + 1];

    const currentGroupKey = isGroupingEnabled ? getValidateGroupKey(current) : "";
    const prevGroupKey = isGroupingEnabled ? getValidateGroupKey(prev) : "";
    const nextGroupKey = isGroupingEnabled ? getValidateGroupKey(next) : "";

    const hasTestPlan = currentGroupKey !== "";
    const isStartOfGroup = hasTestPlan && (!prev || currentGroupKey !== prevGroupKey);
    const isEndOfGroup = hasTestPlan && (!next || currentGroupKey !== nextGroupKey);

    const isSelected = entry?.uniqueId === selectedRow?.CommandUid;
    const prevEntryRef = useRef<ConsoleEntry | null>(null);

    // Pass/Fail result for the whole test plan group
    const groupResult =
      getValidateGroupResult(current, testPlanResults) ??
      testPlanResults?.[getValidateGroupResultKey(current)];

    const groupResults = hasTestPlan
      ? checkGroupHasResults(index, entries, currentGroupKey)
      : { hasPass: false, hasFail: false };

    const isGroupOnboard = hasTestPlan
      ? checkGroupIsOnboard(index, entries, currentGroupKey)
      : false;

    const hasFailedEntry = groupResults.hasFail;
    const hasPassedEntry = groupResults.hasPass;

    // Per-entry Pass/Fail/Timeout handler (persist via API)
    const sendEntryResult = useCallback(async (entryStateKey: string, commandUid: string, result: boolean | 'pass' | 'fail' | 'timeout') => {
      if (!entryStateKey || !commandUid) return;
      // Lock the result so API refreshes don't overwrite it while the call is in flight
      pendingEntryResultsRef.current.set(entryStateKey, result);
      // Optimistic UI update
      setEntries((prev) =>
        prev.map((e) => getEntryResultStateKey(e) === entryStateKey ? { ...e, result } : e)
      );

      try {
        await setTelecommandResult(
          commandUid,
          typeof result === "boolean" ? (result ? "pass" : "fail") : result
        );
        // Keep pending until user explicitly refreshes — WebSocket data must not overwrite.
      } catch (err) {
        // Roll back: remove pending so the reverted value is not re-applied on next refresh
        pendingEntryResultsRef.current.delete(entryStateKey);
        setEntries((prev) =>
          prev.map((e) => getEntryResultStateKey(e) === entryStateKey ? { ...e, result: undefined } : e)
        );
        // eslint-disable-next-line no-console
        console.error("Failed to update telecommand result", err);
        showToastMessage("error", "Failed to update command result");
      }
    }, [setEntries, pendingEntryResultsRef]);

    const borderStyle =
      entry?.CommandDetails?.tctmType === "TCTM"
        ? { borderLeft: entry.type === "Tc" ? "2px solid #CCF54E" : "2px solid #36F097" }
        : {};

    const handleRowClick = useCallback(() => {
      if (!entry) return;
      removeCommandUidForHighlight(entry?.CommandUid);
      const isSameRow = prevEntryRef.current?.CommandUid === entry?.CommandUid;
      if (!isSameRow) {
        setIsFrozen(true);
        setDataCount(0);
        setIsOutputModalVisible(true);
      } else {
        const nextModalState = !isOutputModalVisible;
        setIsFrozen(nextModalState);
        setIsOutputModalVisible(nextModalState);
      }
      if (entry) {
        getCommandDetails(entry);
        show_selected_output_details(entry, entries);
      }
      prevEntryRef.current = entry ?? null;
    }, [
      entry, entries, isOutputModalVisible,
      listRef, getCommandDetails, setDataCount, setIsFrozen, setIsOutputModalVisible,
      show_selected_output_details, removeCommandUidForHighlight,
    ]);

    const tctmType = entry?.["CommandDetails"]?.tctmType as string | undefined;
    const isHighlighted = highlightedCommands.has(entry?.CommandUid ?? "") && entry?.type === "Tc";
    const rowClass = showCorrespondingTask
      ? "table__body__row_message"
      : `table__body__row ${isHighlighted ? "highlight-blink" : ""} ${TCTM_TYPE_CLASSES[tctmType ?? ""] ?? ""}`;

    return (
      <div
        style={{
          ...style,
          borderLeft: hasTestPlan && !isStartOfGroup ? "0.5px solid #5174A3" : "none",
          borderRight: hasTestPlan && !isStartOfGroup ? "0.5px solid #5174A3" : "none",
          borderBottom: hasTestPlan && isEndOfGroup ? "0.5px solid #5174A3" : "none",
          background: hasTestPlan ? "#1A1D21" : "transparent",
          borderRadius: hasTestPlan && isEndOfGroup ? "0px 0px 4px 4px" : "0px",
        }}
      >
        {/* Test-plan group header row (validate mode only) */}
        {hasTestPlan && isStartOfGroup && isValidateMode && (
          <TestPlanHeader
            entry={current!}
            result={groupResult}
            hasFailedEntry={hasFailedEntry}
            hasPassedEntry={hasPassedEntry}
            isOnboard={isGroupOnboard}
          />
        )}

        <div
          className={rowClass}
          style={{
            borderRight: hasTestPlan && isStartOfGroup ? "0.5px solid #5174A3" : "none",
            borderLeft: hasTestPlan && isStartOfGroup ? "0.5px solid #5174A3" : "none",
            width: "max-content",
            minWidth: "100%",
            display: "flex",
          }}
        >
          <button
            type="button"
            className={`table__body__row__item ${isSelected
                ? entry?.type === "Tc"
                  ? "selected-request"
                  : "selected-response"
                : ""
              }`}
            onClick={() => { if (!showCorrespondingTask) handleRowClick(); }}
            style={{ ...borderStyle, width: "100%", minWidth: "100%" }}
          >
            {entry &&
              filteredColumns.map((item) => (
                <TableCell
                  key={item.label}
                  width={item.width}
                  content={
                    item.label === "TcTmType"
                      ? (item.value(entry) as string) || entry?.type || (entry?.RequestType === "Telemetry" ? "TM" : "TC")
                      : item.value(entry)
                  }
                  align={item.label === "TcTmType" ? "center" : "start"}
                >
                  {/* Results cell — read-only: always show backend result */}
                  {item.label === "Results" && currentGroupKey && isValidateMode ? (
                    entry?.result !== undefined ? (
                      entry.result === "pass" || entry.result === true ? (
                        <span className="console-result console-result--pass">Pass</span>
                      ) : entry.result === "timeout" ? (
                        <span className="console-result console-result--timeout">Timeout</span>
                      ) : (
                        <span className="console-result console-result--fail">Fail</span>
                      )
                    ) : (
                      <span className="console-result console-result--empty">-</span>
                    )
                  ) : item.label === "Results" ? (
                    <span className="console-result console-result--empty">-</span>
                  ) : item.label === "Info" ? (
                    (() => {
                      const infoVal = item.value(entry);
                      const infoText = infoVal !== undefined && infoVal !== null && infoVal !== "" ? String(infoVal) : "--";
                      return (
                        <CustomToolTip
                          title={infoText}
                          placement="top"
                          className="console-table-cell__tooltip"
                        >
                          <span
                            className="truncate__text"
                            style={{ minWidth: 0, width: "100%", display: "block" }}
                          >
                            {infoText}
                          </span>
                        </CustomToolTip>
                      );
                    })()
                  ) : (
                    <>
                      <span className="tcd-direction-icon-wrapper">
                        {item.renderIcon?.(entry) && (
                          <CustomToolTip title={String((item.value(entry) as string) || entry?.type || (entry?.RequestType === "Telemetry" ? "TM" : "TC"))} placement="top">
                            <span className="tcd-dir-icon-cell">
                              {item.renderIcon(entry)}
                            </span>
                          </CustomToolTip>
                        )}
                        {(entry?.isCritical || deriveIsCritical(entry)) && item.renderIcon && (
                          <CustomToolTip title="Critical Command" placement="top">
                            <span className="tcd-critical-icon-cell">
                              <CriticalIcon />
                            </span>
                          </CustomToolTip>
                        )}
                      </span>
                      {item.label === "TcTmType" ? null : item.value(entry)}
                    </>
                  )}
                </TableCell>
              ))}
          </button>
        </div>
      </div>
    );
  }
);
Row.displayName = "ConsoleOutputRow";

export { Row };

// ─── ConsoleOutputList props ──────────────────────────────────────────────────

export interface ConsoleOutputListProps {
  height: number;
  getCommandDetails: (commandId: { CommandUid?: string }) => void;
  show_selected_output_details: (selected_command: ConsoleEntry | null, entries: ConsoleEntry[]) => void;
  selectedRow: SelectedRowState;
  isOutputModalVisible: boolean;
  setIsOutputModalVisible: (v: boolean) => void;
  consoleTableColumns: ConsoleTableColumn[];
  setEntries: React.Dispatch<React.SetStateAction<ConsoleEntry[]>>;
  entries: ConsoleEntry[];
  allFilters: AllFilterType;
  setIsFrozen: (v: boolean) => void;
  isFrozen: boolean;
  dataCount: number;
  setDataCount: React.Dispatch<React.SetStateAction<number>>;
  setSelectedRow: React.Dispatch<React.SetStateAction<SelectedRowState>>;
  /** Test-plan level Pass/Fail results (validate mode only). */
  testPlanResults: Record<string, boolean | string>;
  setTestPlanResults: React.Dispatch<React.SetStateAction<Record<string, boolean | string>>>;
  filteredColumns?: ConsoleTableColumn[];
  onTableScroll?: (e: any) => void;
  outerRef?: React.RefObject<HTMLDivElement>;
}

// ─── ConsoleTableInnerElement ──────────────────────────────────────────────────

const ConsoleTableInnerElement = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ style, ...rest }, ref) => (
    <div
      ref={ref}
      style={{
        ...style,
        width: "max-content",
        minWidth: "100%",
        paddingRight: "8px",
        boxSizing: "border-box",
      }}
      {...rest}
    />
  )
);
ConsoleTableInnerElement.displayName = "ConsoleTableInnerElement";

// ─── ConsoleOutputList ────────────────────────────────────────────────────────

/** Virtualized list of console messages with WebSocket updates, pagination, freeze support,
 *  and test-plan grouping for validate deployment mode. */
const ConsoleOutputList = memo<ConsoleOutputListProps>(function ConsoleOutputList({
  height,
  getCommandDetails,
  show_selected_output_details,
  selectedRow,
  isOutputModalVisible,
  setIsOutputModalVisible,
  consoleTableColumns,
  setEntries,
  entries,
  allFilters,
  setIsFrozen,
  isFrozen,
  dataCount,
  setDataCount,
  setSelectedRow,
  testPlanResults,
  setTestPlanResults,
  filteredColumns: propsFilteredColumns,
  onTableScroll,
  outerRef,
}) {
  const { truetwin_id } = useTrueTwin();
  const { sat_id, scenario_id, dashboard } = useUrlParams();
  const { scenario } = useScenario();
  const { highlightedCommands, removeCommandUidForHighlight } = useConsole();
  const [showScrollTop, setShowScrollTop] = useState(false);
  // Setter-only state passed to fetchEntries as a loading callback
  const [, setCommandLoading] = useState(false);
  const loadingRef = useRef(false);
  const listRef = useRef<List>(null);
  const cursorRef = useRef("");
  const isFirstRender = useRef(true);
  const isAtTopRef = useRef(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const isFrozenRef = useRef(isFrozen);

  const checkIsAtTop = useCallback((): boolean => {
    const list = listRef.current as any;
    const outerEl =
      (outerRef as any)?.current ??
      list?._outerRef ??
      list?.outerRef ??
      list?._listRef;
    if (outerEl && typeof outerEl.scrollTop === "number") {
      const atTop = outerEl.scrollTop <= 5;
      isAtTopRef.current = atTop;
      return atTop;
    }
    if (list?.state && typeof list.state.scrollOffset === "number") {
      const atTop = list.state.scrollOffset <= 5;
      isAtTopRef.current = atTop;
      return atTop;
    }
    return isAtTopRef.current;
  }, [outerRef]);
  const [isDataFound, setIsDataFound] = useState(false);
  const [frozenData, setFrozenData] = useState<ConsoleEntry[] | null>(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const isWsLoadingRef = useRef(false);
  const isWsPendingRef = useRef(false);
  const allFiltersRef = useRef(allFilters);
  /** Per-entry pending results: CommandUid → result. Prevents API refresh from wiping optimistic state. */
  const pendingEntryResultsRef = useRef<Map<string, boolean | string>>(new Map());
  /** Group-level pending results: testPlanId → result. Same purpose as above. */
  const pendingGroupResultsRef = useRef<Map<string, boolean | string>>(new Map());

  const isOperateDashboard = useMemo(() => {
    const depType = scenario?.scenarioDeploymentType?.toUpperCase();
    if (depType === SCENARIO_DEPLOYMENT_TYPES.OPERATE || depType === "OPERATE") return true;
    if (typeof window !== "undefined") {
      const path = window.location.pathname.toLowerCase();
      if (path.includes("operate-dashboard")) return true;
    }
    return false;
  }, [scenario?.scenarioDeploymentType]);

  /** True when this console is running inside a validate scenario */
  const isValidateMode = useMemo(() => {
    const depType = scenario?.scenarioDeploymentType?.toUpperCase();
    if (depType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE || depType === "VALIDATE") return true;
    if (typeof window !== "undefined") {
      const path = window.location.pathname.toLowerCase();
      if (path.includes("/validate") || path.includes("/validation")) return true;
    }
    if (entries && entries.length > 0 && entries.some(hasValidateGroupMetadata)) {
      return true;
    }
    return false;
  }, [scenario?.scenarioDeploymentType, entries]);

  /** True when test-plan grouping should be rendered: validate scenario and NOT operate dashboard */
  const isGroupingEnabled = isValidateMode && !isOperateDashboard;

  const scenario_completed = useMemo(
    () => scenario?.status === "SimulationCompleted",
    [scenario?.status]
  );

  useEffect(() => {
    allFiltersRef.current = allFilters;
  }, [allFilters]);

  useEffect(() => {
    isFrozenRef.current = isFrozen;
  }, [isFrozen]);

  // ── Use backend-provided testPlan grouping when available ──────────────────
  const entriesWithTestPlan = useMemo<ConsoleEntry[]>(() => entries ?? [], [entries]);

  //listen that if filter updated --> needs for API integration
  useLayoutEffect(() => {
    const handleFilterUpdate = (e) => {
      setIsFilterLoading(true);
      loadLatestEntries(e.detail, true, false);
    };
    window.addEventListener("postFilters", handleFilterUpdate);
    return () => {
      window.removeEventListener("postFilters", handleFilterUpdate);
    };
  }, []);

  // Fetches the most recent entries from the API, replacing the current list
  const loadLatestEntries = useCallback(
    async (filters: AllFilterType, isFilterApplied: boolean, fromWs: boolean) => {
      if (isFrozen) return;
      if (fromWs && !checkIsAtTop()) {
        setDataCount((prev) => (prev ?? 0) + 1);
        return;
      }
      loadingRef.current = true;
      if (fromWs && isWsLoadingRef.current) {
        isWsPendingRef.current = true;
        return;
      }
      if (fromWs) isWsLoadingRef.current = true;
      try {
        const { data, cursor } = await fetchEntries(
          sat_id ?? "",
          truetwin_id ?? "",
          scenario_id ?? "",
          "",
          filters,
          isFilterApplied,
          setCommandLoading,
          false
        );

        // User-initiated refresh (scroll-to-top, filter apply, etc.) — treat as
        // "see latest data" intent, so clear any locally-pinned Pass/Fail state first.
        if (!fromWs) {
          pendingEntryResultsRef.current.clear();
          pendingGroupResultsRef.current.clear();
        }

        // Overlay any still-pending per-entry results on top of fresh API data
        // so a Pass/Fail click is not wiped out by background WebSocket refreshes.
        const mergedData = (data as ConsoleEntry[]).map((entry) =>
          pendingEntryResultsRef.current.has(getEntryResultStateKey(entry))
            ? { ...entry, result: pendingEntryResultsRef.current.get(getEntryResultStateKey(entry)) }
            : entry
        );

        setEntries(mergedData);
        setIsDataFound(true);
        cursorRef.current = cursor;
        sessionStorage.setItem("globalCursor", cursor);

        // Group header state must not be inferred from per-entry command results.
        // A telecommand row can be PASS/FAIL while the testcase/group result is
        // still unset or different. Only preserve explicit group-level actions.
        const seeded: Record<string, boolean | string> = {};
        // Overlay pending group results so a group-level click also survives refresh.
        pendingGroupResultsRef.current.forEach((result, testPlanId) => {
          seeded[testPlanId] = result;
        });
        setTestPlanResults(seeded);
      } finally {
        loadingRef.current = false;
        setIsFilterLoading(false);
        if (fromWs) {
          isWsLoadingRef.current = false;
          if (isWsPendingRef.current) {
            isWsPendingRef.current = false;
            if (checkIsAtTop() && !isFrozenRef.current) {
              setDataCount(0);
              loadLatestEntries(allFiltersRef.current, false, true);
            } else {
              setDataCount((prev) => (prev ?? 0) + 1);
            }
          }
        }
      }
    },
    [sat_id, truetwin_id, scenario_id, isFrozen, setEntries, setTestPlanResults, checkIsAtTop, setDataCount]
  );

  const loadLatestEntriesRef = useRef(loadLatestEntries);
  loadLatestEntriesRef.current = loadLatestEntries;

  // Appends older entries using cursor-based pagination when scrolling down
  const loadMoreEntries = useCallback(
    async (filters: AllFilterType, isFilterApplied: boolean) => {
      if (loadingRef.current || !cursorRef.current) return;
      loadingRef.current = true;
      try {
        const { data, cursor } = await fetchEntries(
          sat_id ?? "",
          truetwin_id ?? "",
          scenario_id ?? "",
          cursorRef.current,
          filters,
          isFilterApplied,
          setCommandLoading,
          true
        );
        setEntries((prev) => [...prev, ...(data as ConsoleEntry[])]);
        cursorRef.current = cursor;
        sessionStorage.setItem("globalCursor", cursor);
      } finally {
        loadingRef.current = false;
      }
    },
    [sat_id, truetwin_id, scenario_id, setEntries]
  );

  // Triggers load-more at bottom and refresh at top based on scroll position
  const handleScroll = useCallback(
    ({ scrollOffset, scrollDirection }: { scrollOffset: number; scrollDirection: "forward" | "backward" }) => {
      setShowScrollTop(scrollOffset > 10);
      const atTop = scrollOffset <= 5;
      isAtTopRef.current = atTop;
      setIsAtTop((prev) => (prev !== atTop ? atTop : prev));

      if (atTop && dataCount > 0) {
        setDataCount(0);
      }

      const list = listRef.current;
      if (!list) return;
      const totalHeight = (entriesWithTestPlan ?? []).reduce(
        (sum, _, i) => sum + getItemSize(i, entriesWithTestPlan ?? [], isGroupingEnabled),
        0
      );
      const bottomThreshold = 5;
      if (!isFrozen) {
        if (
          scrollDirection === "forward" &&
          scrollOffset + Number(list.props?.height ?? 0) >= totalHeight - bottomThreshold
        ) {
          loadMoreEntries(allFilters, false);
        } else if (scrollDirection === "backward" && atTop) {
          loadLatestEntries(allFilters, false, false);
        }
      }
    },
    [entriesWithTestPlan, allFilters, isFrozen, dataCount, setDataCount, loadMoreEntries, loadLatestEntries, isGroupingEnabled]
  );

  useEffect(() => {
    const token = sessionStorage.getItem("authentication");
    if (!truetwin_id || !token) return;
    const sessionId = uuidv4();
    const ws = new WebSocket(
      `${getEnvVar("REACT_APP_WEBSOCKET_BACKEND_BASE_URL")}groundstation/websocketstatus?truetwinId=${truetwin_id}&sessionId=${sessionId}`
    );
    ws.onopen = () => ws.send(JSON.stringify({ type: "authorization", token }));
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string);
        window.dispatchEvent(new CustomEvent("validation_ws_message", { detail: msg }));
        const count = msg?.count > 0 ? msg.count : 1;
        const isReloadEvent = isValidateReloadEvent(msg);
        if (isReloadEvent) {
          const atTop = checkIsAtTop();
          if (atTop && !isFrozenRef.current) {
            setDataCount(0);
            loadLatestEntriesRef.current(allFiltersRef.current ?? {}, false, true);
          } else {
            setDataCount((prev) => (prev ?? 0) + count);
          }
        }
      } catch (err) {
        console.error("Error parsing websocket message:", err);
      }
    };
    ws.onerror = console.error;
    return () => ws.close();
  }, [truetwin_id, setDataCount, checkIsAtTop]);

  useEffect(() => {
    loadLatestEntries(allFilters ?? {}, true, false);
  }, [truetwin_id]);

  // Scrolls to top, refreshes entries, and resets unread count
  const handleBtnClick = useCallback(() => {
    isAtTopRef.current = true;
    setIsAtTop(true);
    handleUnreadBadgeClick({
      listRef,
      loadLatestEntries,
      allFilters,
      setDataCount,
      setIsFrozen,
    });
  }, [allFilters, loadLatestEntries, setDataCount, setIsFrozen]);

  // Blocks wheel / touch scroll on the list only when output modal is open.
  useEffect(() => {
    const outerEl = (listRef.current as any)?._outerRef as HTMLElement | null;
    if (!outerEl) return;
    if (isFrozen && isOutputModalVisible) {
      const blockScroll = (e: Event) => e.preventDefault();
      outerEl.addEventListener("wheel", blockScroll, { passive: false });
      outerEl.addEventListener("touchmove", blockScroll, { passive: false });
      return () => {
        outerEl.removeEventListener("wheel", blockScroll);
        outerEl.removeEventListener("touchmove", blockScroll);
      };
    }
  }, [isFrozen, isOutputModalVisible]);

  useEffect(() => {
    if (isFrozen && !frozenData) {
      setFrozenData(entriesWithTestPlan);
    } else if (!isFrozen && frozenData) {
      setFrozenData(null);
    }
  }, [isFrozen, entriesWithTestPlan, frozenData]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isFrozen && isAtTopRef.current) {
      setDataCount(0);
      loadLatestEntries(allFiltersRef.current ?? {}, false, false);
    }
    // When unfreezing (e.g. closing details modal), keep scroll position preserved.
  }, [isFrozen, loadLatestEntries, setDataCount]);

  // Pre-filter visible columns once for Row rendering
  const filteredColumns = useMemo(() => {
    if (propsFilteredColumns && propsFilteredColumns.length > 0) {
      return propsFilteredColumns;
    }
    // Validate+Summary me "operate" jaisi columns show honi chahiye (running-session parity).
    const showOperateColumns = dashboard === "operate" || isValidateMode;
    return showOperateColumns
      ? consoleTableColumns.filter((item) => item.isSelected)
      : consoleTableColumns.filter((item) => item.isSelected && item.dashboard !== "operate");
  }, [propsFilteredColumns, consoleTableColumns, dashboard, isValidateMode]);

  // In validate mode, header and rows must both exclude these columns.
  const visibleColumns = useMemo(() => filteredColumns, [filteredColumns]);

  const displayEntries = isFrozen ? frozenData || [] : entriesWithTestPlan;

  const getSelectedRowMetrics = useCallback(
    (targetIndex: number, items: ConsoleEntry[]) => {
      let offsetTop = 0;

      for (let i = 0; i < targetIndex; i += 1) {
        offsetTop += getItemSize(i, items, isGroupingEnabled);
      }

      const currentEntry = items[targetIndex];
      const previousEntry = items[targetIndex - 1];
      const currentGroupKey = isGroupingEnabled ? getValidateGroupKey(currentEntry) : "";
      const previousGroupKey = isGroupingEnabled ? getValidateGroupKey(previousEntry) : "";
      const hasTestPlan = currentGroupKey !== "";
      const isStartOfGroup =
        hasTestPlan && (!previousEntry || currentGroupKey !== previousGroupKey);

      return {
        rowTop: offsetTop + (isStartOfGroup ? GROUP_HEADER_HEIGHT : 0),
        rowHeight: BASE_ROW_HEIGHT,
      };
    },
    [isGroupingEnabled]
  );

  const keepSelectedRowVisible = useCallback(() => {
    if (!isOutputModalVisible) return;
    if (typeof selectedRow.selectedRow !== "number" || selectedRow.selectedRow < 0) return;
    if (selectedRow.selectedRow >= displayEntries.length) return;

    const list = listRef.current as
      | {
        scrollTo?: (offset: number) => void;
        state?: { scrollOffset?: number };
        _outerRef?: HTMLElement | null;
        _listRef?: HTMLElement | null;
        outerRef?: HTMLElement | null;
      }
      | null;

    const outerElement = list?._outerRef ?? list?._listRef ?? list?.outerRef ?? null;
    if (!list || !outerElement) return;

    const { rowTop, rowHeight } = getSelectedRowMetrics(selectedRow.selectedRow, displayEntries);
    // Always use the `height` prop (= listDisplayHeight) instead of outerElement.clientHeight.
    // clientHeight is stale when the output modal opens and shrinks the list — it still
    // reflects the OLD tall value for several frames even after 2 rAF, causing wrong scroll.
    const viewportHeight = height;
    const currentScrollTop = outerElement.scrollTop ?? list.state?.scrollOffset ?? 0;
    const topPadding = 16;
    // Use a fixed bottom clearance (row height + small margin) so the selected row
    // is always fully visible above the output panel regardless of viewport height.
    const bottomPadding = rowHeight + 8;
    let nextScrollTop = currentScrollTop;

    if (rowTop < currentScrollTop + topPadding) {
      nextScrollTop = Math.max(0, rowTop - topPadding);
    } else if (rowTop + rowHeight > currentScrollTop + viewportHeight - bottomPadding) {
      nextScrollTop = rowTop + rowHeight - viewportHeight + bottomPadding;
    }

    if (Math.abs(nextScrollTop - currentScrollTop) > 1) {
      // Pass `list` directly without spreading so we don't drop the prototype methods (like `.scrollTo`).
      // Pass the current scroll top as the 4th argument so smoothScroll knows where to animate from.
      smoothScroll(list as any, nextScrollTop, 220, currentScrollTop);
    }
  }, [
    displayEntries,
    getSelectedRowMetrics,
    height,
    isOutputModalVisible,
    selectedRow.selectedRow,
  ]);

  // Flush react-window's stale size cache whenever entries change.
  // VariableSizeList caches heights by index — without this reset, rows whose
  // group membership changed (isStartOfGroup / isEndOfGroup) keep the old
  // cached height, causing grouped rows to visually overlap or leave gaps.
  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [displayEntries, isGroupingEnabled]);

  useEffect(() => {
    if (!isOutputModalVisible) return;

    let frameOne = 0;
    let frameTwo = 0;
    let frameThree = 0;

    frameOne = requestAnimationFrame(() => {
      frameTwo = requestAnimationFrame(() => {
        frameThree = requestAnimationFrame(() => {
          keepSelectedRowVisible();
        });
      });
    });

    return () => {
      cancelAnimationFrame(frameOne);
      cancelAnimationFrame(frameTwo);
      cancelAnimationFrame(frameThree);
    };
  }, [height, isOutputModalVisible, keepSelectedRowVisible, selectedRow.CommandUid, selectedRow.selectedRow]);

  const itemData: RowItemData = useMemo(
    () => ({
      entries: displayEntries,
      selectedRow,
      getCommandDetails: getCommandDetails as (entry: ConsoleEntry) => void,
      show_selected_output_details,
      consoleTableColumns,
      setIsOutputModalVisible,
      isOutputModalVisible,
      setIsFrozen,
      setDataCount,
      listRef,
      height,
      isFrozen,
      frozenData,
      removeCommandUidForHighlight,
      highlightedCommands,
      dashboard,
      filteredColumns: visibleColumns,
      testPlanResults,
      setTestPlanResults,
      setEntries,
      isValidateMode,
      isGroupingEnabled,
      pendingEntryResultsRef,
      pendingGroupResultsRef,
    }),
    [
      displayEntries,
      selectedRow,
      getCommandDetails,
      show_selected_output_details,
      consoleTableColumns,
      setIsOutputModalVisible,
      isOutputModalVisible,
      setIsFrozen,
      setDataCount,
      height,
      isFrozen,
      frozenData,
      removeCommandUidForHighlight,
      highlightedCommands,
      dashboard,
      visibleColumns,
      testPlanResults,
      setTestPlanResults,
      setEntries,
      isValidateMode,
      isGroupingEnabled,
    ]
  );

  const itemCount = displayEntries?.length ?? 0;

  const handleListScroll = useCallback(
    (e: any) => {
      handleScroll(e);
      if (onTableScroll) {
        const target = e?.target || e?.currentTarget;
        const scrollLeft =
          target && typeof target.scrollLeft === "number"
            ? target.scrollLeft
            : (typeof e?.scrollOffset === "number" ? e.scrollOffset : 0);
        onTableScroll(scrollLeft);
      }
    },
    [handleScroll, onTableScroll]
  );

  return (
    <>
      {(!isDataFound || isFilterLoading) && <ShimmerMenuList />}
      {!scenario_completed && dataCount > 0 && (!isAtTop || !isAtTopRef.current) && (
        <div
          className="unread_messages_div"
          onClick={handleBtnClick}
          role="button"
          tabIndex={0}
        >
          <span key={dataCount}>{dataCount + (dataCount === 1 ? " new message" : " new messages")}</span>
        </div>
      )}
      <div
        className={`output__div__message__container ${(entries?.length === 0 || !entries) && isDataFound && !isFilterLoading ? "d-flex" : "d-none"
          }`}
      >
        No data found.
      </div>
      <List
        ref={listRef}
        outerRef={outerRef as any}
        itemData={itemData}
        height={height}
        itemCount={itemCount}
        itemSize={(index) => getItemSize(index, displayEntries, isGroupingEnabled)}
        width="100%"
        innerElementType={ConsoleTableInnerElement}
        onScroll={handleListScroll}
        overscanCount={5}
        className={`row_change_position_console_table modal__scrollbar__white__vertical modal__scrollbar__white__horizontal ${
          isFrozen ? "is-frozen" : ""
        }`}
      >
        {Row}
      </List>
      <button
        type="button"
        onClick={handleBtnClick}
        title="Scroll to top"
        className={`scroll-to-top ${showScrollTop && !isFrozen ? "" : "hidden"} ${isOutputModalVisible ? "" : "scroll-to-top-middle"
          }`}
      >
        <ArrowUp />
      </button>
    </>
  );
});

export { ConsoleOutputList };
